import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { adminFetch } from '../utils/adminFetch';

interface ActiveVerificationCode {
  id: string;
  email: string;
  code: string;
  attempts: number;
  createdAt: string;
  expiresAt: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatRemainingTime = (expiresAt: string): string => {
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return 'Expiré';
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  if (minutes > 0) return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
  return `${seconds} s`;
};

const VerificationCodes: React.FC = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [codes, setCodes] = useState<ActiveVerificationCode[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [, setTick] = useState(0);

  const fetchCodes = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/verification-codes/active`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Impossible de charger les codes actifs');
      }
      const payload = await response.json();
      setCodes(payload.codes ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les codes actifs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    void fetchCodes();
  }, [fetchCodes]);

  useEffect(() => {
    const interval = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCopyCode = async (id: string, code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Impossible de copier le code dans le presse-papiers');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Codes de connexion actifs
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void fetchCodes(true)}
          disabled={refreshing}
        >
          Actualiser
        </Button>
      </Box>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{ mb: 3 }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Règles des codes de vérification
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            Chaque code est actif pendant <strong>24 heures</strong> après sa génération.
          </li>
          <li>
            Demander un <strong>nouveau code</strong> n&apos;invalide pas les codes précédents :
            tous les codes non utilisés restent valides jusqu&apos;à expiration (ou jusqu&apos;à
            une connexion réussie).
          </li>
        </Box>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {codes.length === 0 ? (
            <Box p={3}>
              <Typography color="text.secondary">
                Aucun code de connexion actif pour le moment.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Apprenant</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Demandé le</TableCell>
                    <TableCell>Expire le</TableCell>
                    <TableCell>Temps restant</TableCell>
                    <TableCell align="right">Tentatives</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codes.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        {entry.student
                          ? `${entry.student.firstName} ${entry.student.lastName}`
                          : '—'}
                      </TableCell>
                      <TableCell>{entry.email}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={entry.code}
                            size="small"
                            sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                          />
                          <Tooltip title={copiedId === entry.id ? 'Copié !' : 'Copier le code'}>
                            <IconButton
                              size="small"
                              onClick={() => void handleCopyCode(entry.id, entry.code)}
                              aria-label="Copier le code"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(entry.expiresAt)}</TableCell>
                      <TableCell>{formatRemainingTime(entry.expiresAt)}</TableCell>
                      <TableCell align="right">{entry.attempts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VerificationCodes;
